import { BaseApiService } from '../BaseApiService';
import { ApiResponse } from '../ApiResponse';
import { apiUrls } from '../ApiUrls';

export interface SubscriptionPlan {
  id: number;
  subscriptionId: number;
  subscriptionCode: string | null;
  subscriptionName: string | null;
  subscriptionDescription: string | null;
  subscriptionRoleId: number | null;
  subscriptionRoleName: string | null;
  recommended: boolean | null;
  planId: number | null;
  planCode: string | null;
  planName: string | null;
  durationDays: number | null;
  price: number | null;
  isFree: boolean;
  status: string | null;
  isActive: boolean;
  features: SubscriptionFeatureItem[];
  billingCycles: SubscriptionBillingCycle[];
}

export interface SubscriptionBillingCycle {
  id: number;
  billingCycle: number;
  price: number;
  discountAmount: number;
  isFree: boolean;
  sequence: number;
}

export interface SubscriptionFeatureItem {
  id: number;
  featureId: string;
  name: string;
  description: string | null;
  apiUrl: string | null;
  isActive: boolean;
}

export interface PaymentInitiateRequest {
  subscriptionPlanId: number;
  billingCycleId?: number;
  returnUrl: string;
}

export interface PaymentInitiateResponse {
  paymentTransactionId: string;
  checkoutHtml: string | null;
  subscriptionPlanId: number;
  subscriptionName: string | null;
  planName: string | null;
  amount: number;
  isFree: boolean;
  /** Set when the plan/cycle was free — subscription was granted immediately, no gateway needed. */
  subscriptionCompleted?: boolean;
  subscription: { id: number; code: string; name: string } | null;
  features?: Record<string, unknown>;
  token?: string;
  refreshToken?: string;
  subscriptionStartDate?: string | null;
  subscriptionExpiryDate?: string | null;
}

export interface PaymentConfirmRequest {
  paymentTransactionId: string;
  subscriptionPlanId: number;
  billingCycleId?: number;
}

export interface PaymentConfirmResponse {
  subscriptionPlanId: number;
  subscriptionName: string | null;
  planName: string | null;
  paymentStatus: string;
  subscription: { id: number; code: string; name: string } | null;
  features: Record<string, unknown>;
  token: string;
  refreshToken: string;
  subscriptionStartDate?: string | null;
  subscriptionExpiryDate?: string | null;
}

export interface CurrentUserSubscription {
  subscriptionPlanId: number | null;
  subscriptionId: number | null;
  subscriptionName: string | null;
  planName: string | null;
  startDate: string | null;
  endDate: string | null;
  paidAmount: number | null;
  dueAmount: number | null;
  statusCode: string | null;
  statusValue: string | null;
}

export interface SubscriptionPaymentHistoryItem {
  paymentTransactionCode: string | null;
  subCategory: string | null;
  payableAmount: number | null;
  discountAmount: number | null;
  paidAmount: number | null;
  outstandingAmount: number | null;
  transactionStatus: string | null;
  paymentStatusValue: string | null;
  createdAt: string | null;
}

export interface SubscriptionCancelRequest {
  effectiveDate: string;
  reasonCode: string;
  comments?: string;
}

export interface UserSubscriptionHistoryItem {
  userSubscriptionId: string | null;
  subscriptionName: string | null;
  planName: string | null;
  startDate: string | null;
  endDate: string | null;
  statusCode: string | null;
  statusValue: string | null;
  paidAmount: number | null;
  totalPayableAmount: number | null;
  discountAmount: number | null;
  paymentStatusCode: string | null;
  paymentStatusValue: string | null;
  cancellationDate: string | null;
  cancellationReason: string | null;
  createdAt: string | null;
}

class SubscriptionService extends BaseApiService {

  static async getActivePlans(role?: string): Promise<ApiResponse<SubscriptionPlan[]>> {
    try {
      const url = role
        ? `${apiUrls.subscription.plans}?role=${encodeURIComponent(role)}`
        : apiUrls.subscription.plans;
      return await SubscriptionService.get(url, 'Get Subscription Plans');
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to fetch subscription plans')
      );
    }
  }

  static async getCurrentSubscription(): Promise<ApiResponse<CurrentUserSubscription | null>> {
    try {
      return await SubscriptionService.getAuthenticated(
        apiUrls.subscription.myCurrent,
        'Get Current Subscription'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to fetch current subscription')
      );
    }
  }

  static async getFreePlanStatus(): Promise<ApiResponse<boolean>> {
    try {
      return await SubscriptionService.getAuthenticated(
        apiUrls.subscription.freePlanStatus,
        'Get Free Plan Status'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to fetch free plan status')
      );
    }
  }

  static async cancelSubscription(
    request: SubscriptionCancelRequest
  ): Promise<ApiResponse<CurrentUserSubscription | null>> {
    try {
      return await SubscriptionService.postAuthenticated(
        apiUrls.subscription.cancel,
        request,
        'Cancel Subscription'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to cancel subscription')
      );
    }
  }

  static async getSubscriptionHistory(): Promise<ApiResponse<UserSubscriptionHistoryItem[]>> {
    try {
      return await SubscriptionService.getAuthenticated(
        apiUrls.subscription.history,
        'Get Subscription History'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to fetch subscription history')
      );
    }
  }

  static async getPaymentHistory(): Promise<ApiResponse<SubscriptionPaymentHistoryItem[]>> {
    try {
      return await SubscriptionService.getAuthenticated(
        apiUrls.subscription.paymentHistory,
        'Get Subscription Payment History'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to fetch payment history')
      );
    }
  }

  static async initiatePayment(
    request: PaymentInitiateRequest
  ): Promise<ApiResponse<PaymentInitiateResponse>> {
    try {
      return await SubscriptionService.postAuthenticated(
        apiUrls.subscription.paymentInitiate,
        request,
        'Initiate Subscription Payment'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to initiate subscription payment')
      );
    }
  }

  static async confirmPayment(
    request: PaymentConfirmRequest
  ): Promise<ApiResponse<PaymentConfirmResponse>> {
    try {
      return await SubscriptionService.postAuthenticated(
        apiUrls.subscription.paymentConfirm,
        request,
        'Confirm Subscription Payment'
      );
    } catch (error: any) {
      throw new Error(
        SubscriptionService.extractApiErrorMessage(error, 'Failed to confirm subscription payment')
      );
    }
  }
}

export default SubscriptionService;
